/**
 * Module is for handling of queryParams in redirection urls.
 * Mechanism is started if 'redirection_params' are defined in shortcode -> in result in calculator-table
 * exist 'data-redirection-params' attribute. The attribute contains 'list' of 'queryParamName'
   and 'FilterInputName'.

 * 'QueryParamName' is name which will be visiblie in QueryString for specific 'queryParam'.
 * 'FilterInputName' is name of filter input necessary for getting into current value of the input.
 *
 *    Example : -> finder.com/home-loans
 *
 *    'data-redirection-params': "borrowingAmount: hlPrincipal, period: hlPeriod, loanPurpose: hlPurpose"
 *
 *    QueryParamNames:    'borrowingAmount',  'period',     'loanPurpose'
 *                              |                 |               |
 *    FilterInputNames:   'hlPrincipal',      'hlPeriod',   'hlPurpose'
 */

import $ from 'jquery';

const SELECTORS = {
  CALCULATOR_FILTERS: '.calculator-form, .js-table-widget-calculator',
  ENABLED_FILTERS_INPUTS: `
    .form-widget--filter [name]:not(disabled),
    .js-table-widget-filter [name]:not(disabled)
  `,
  CTA_BTNS: '.comparison-table__cta .luna-button--cta',
};

let $calculatorTable = null;
let queryFilters = {};
let _areRedirectionParamsDefined;

/**
 * Start handling redireciton query filters
 *
 * @param {jQuery} $calculator - comparison-calculator table element
 */
function initRedirectionQueryFilters($calculator) {
  const $calculatorFilters = $calculator.find(SELECTORS.CALCULATOR_FILTERS);

  $calculatorTable = $calculator;
  $calculatorFilters.on('change submit', updateQueryParamFilter);

  setInitialQueryFiltersState();
  updateRedirectionUrlsWithQueryParams();
}

/**
 * Check exisitence of redirection param's definition.
 *
 * @param {jQuery} $calculator
 * @return {boolean}
 */
function areRedirectionParamsDefined($calculator = $calculatorTable) {
  if (_areRedirectionParamsDefined === undefined) {
    const redirectionParams = $calculator && $calculator.attr('data-redirection-params');
    _areRedirectionParamsDefined = !!(redirectionParams && redirectionParams.length);
  }

  return _areRedirectionParamsDefined;
}

/**
 * Parse redirection params definition from data attribute.
 *
 * @return {Array<Array<string>>}
 */
function parseRedirectionParamsDef() {
  const redirectionParamsDef = $calculatorTable.attr('data-redirection-params');

  return redirectionParamsDef.replace(/\s+/g, '')
    .split(',')
    .map(rp => rp.split(':'));
}

/**
 * Set initial query filters state based on current value of filter inputs
 */
function setInitialQueryFiltersState() {
  const queryFiltersDefinition = parseRedirectionParamsDef();

  queryFilters = queryFiltersDefinition.reduce((acc, [queryParamName, filterInputName]) => {
    const $filter = $(`[name="${filterInputName}"]`);

    if ($filter.length === 0) {
      logFilterNotExistError(queryParamName, filterInputName);
    }

    return Object.assign({}, acc, { [filterInputName]: {
      name: queryParamName,
      value: getQueryFilterValue($filter),
    } });
  }, {});
}

/**
 * Update query param value based on change of query param filter's input
 *
 * @param {Event} event
 * @param {boolean} shouldUpdateUrls
 */
function updateQueryParamFilter({ target }, shouldUpdateUrls = true) {
  const $filter = $(target);
  const filterName = $filter.attr('name');
  const filterValue = getQueryFilterValue($filter);

  if (queryFilters[filterName]) {
    queryFilters = $.extend(true, {}, queryFilters, {
      [filterName]: {
        value: filterValue,
      },
    });

    if (shouldUpdateUrls) {
      updateRedirectionUrlsWithQueryParams();
    }
  }
}

/**
 * Update redirection urls with new query params
 */
function updateRedirectionUrlsWithQueryParams() {
  $calculatorTable.find(SELECTORS.CTA_BTNS).each((index, btn) => {
    const url = $(btn).attr('href');
    const isRedirectBtn = !!url.length;

    if (isRedirectBtn) {
      const redirectUrl = prepareRedirectionUrlWithQueryParams(url);
      $(btn).attr('href', redirectUrl);
    }
  });
}

/**
 * Get object values
 */
function getObjectValues(obj) {
  if (Object.values) {
    return Object.values(obj);
  }

  // Fallback for IE11
  return Object.keys(obj).map(key => obj[key]);
}

/**
 * Prepare redirection url based on current state of query filters's inputs
 *
 * @param {string} currentUrl
 * @return {string}
 */
function prepareRedirectionUrlWithQueryParams(currentUrl) {
  const url = clearUrl(currentUrl);

  return [url, getObjectValues(queryFilters)
    .filter(({ value }) => value.length)
    .map(({ name, value }) => `${name}=${value}`).join('&'),
  ].join('?');
}

function getQueryFilterValue($input) {
  const isFormatted = $input.is('[data-autoformat-unformat-val]');
  return isFormatted ? $input.attr('data-autoformat-unformat-val') : $input.val();
}
/**
 * Clear url - without query string,
 *
 * @param {string} url
 * @return {string}
 */
function clearUrl(url) {
  return url.split('?')[0];
}

/**
 * Log error about wrong name of filter input
 *
 * @param {string} queryParamName
 * @param {string} filterInputName
 */
function logFilterNotExistError(queryParamName, filterInputName) {
  console.log(
    `REDIRECTION QUERY PARAMS: Wrong filter's input name.
    The filter input doesn't exist: name: ${filterInputName} dedicated for queryParam: ${queryParamName}.`,
  );
}

export default initRedirectionQueryFilters;
export {
  updateQueryParamFilter,
  areRedirectionParamsDefined,
  updateRedirectionUrlsWithQueryParams as updateRedirectionUrls,
};
